import { Small } from '@casavo/base-ui'
import cn from 'classnames'
import { kebabCase } from 'lodash'
import useTranslation from 'next-translate/useTranslation'
import React, { ReactElement } from 'react'

import { linkBuilder } from '@/components/Link/linkBuilder'
import { ContextValue } from '@/context/Cities'
import { useCityPage } from '@/context/CityPageContext'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import getListingsBreadcrumbs from '@/utils/breadcrumbs'
import getTranslatedTitle from '@/utils/get-translated-title'
import { handleListingPageClick } from '@/utils/tracking/eventsHandlers'

import Breadcrumb from './Breadcrumb'
import { breadcrumbsContainer, breadcrumbsLink, breadcrumbsSeparator } from './styles.css'

export const BREADCRUMBS_ITEM_TEST_ID = 'breadcrumbs-item'
type Props = {
  cities: ContextValue
  className?: string
  isOverflow?: boolean
  property?: ListingCate
}

const Breadcrumbs: React.FC<Props> = ({ cities, isOverflow, property, ...props }): ReactElement => {
  const { lang, t } = useTranslation()
  const handleListingPageBreadcrumbClick = () => handleListingPageClick('Breadcrumb') // TODO: check usage

  const hasProperty = typeof property !== 'undefined'
  const breadcrumbs = getListingsBreadcrumbs(
    t,
    lang,
    hasProperty,
    cities.list,
    cities.router.area,
    cities.router.city,
    cities.router.zone
  )

  const { openBreadcrumbModal } = useCityPage()

  return (
    <div className={breadcrumbsContainer} {...props}>
      {/* houses-for-sale page */}
      <a
        className={cn(
          breadcrumbsLink,
          sprinkles({
            display: {
              desktop: 'inline-block',
              mobile: Boolean(property || !cities.router.city) ? 'inline-block' : 'none',
              tablet: 'inline-block',
            },
          })
        )}
        data-testid={BREADCRUMBS_ITEM_TEST_ID}
        href={linkBuilder.housesForSaleCasavoWebsite.url(lang)}
        onClick={handleListingPageBreadcrumbClick}
      >
        <Small>{t('common:footer-our-properties-label')}</Small>
      </a>

      <BreadcrumbsSeparator showOnMobile={Boolean(property || !cities.router.city)} />

      {/* area, city, zone */}
      {breadcrumbs.length > 0 &&
        breadcrumbs.map(({ href, isLast, isLink, label, showOnMobile }) => (
          <React.Fragment key={kebabCase(label)}>
            <Breadcrumb
              href={href}
              isLink={isLink}
              showOnMobile={showOnMobile}
              onClick={!isLink ? openBreadcrumbModal : undefined}
            >
              {label}
            </Breadcrumb>
            <BreadcrumbsSeparator rotateDown={isLast} showOnMobile={showOnMobile} />
          </React.Fragment>
        ))}

      {/* property */}
      {property && (
        <Breadcrumb data-testid={BREADCRUMBS_ITEM_TEST_ID} isLink={false} isOverflow={isOverflow} showOnMobile={true}>
          {getTranslatedTitle(property.detail?.titles, lang)}
        </Breadcrumb>
      )}
    </div>
  )
}

export const BreadcrumbsSeparator: React.FC<{ rotateDown?: boolean; showOnMobile?: boolean }> = ({
  rotateDown,
  showOnMobile,
}) => (
  <div
    className={cn(
      breadcrumbsSeparator({ rotateDown }),
      sprinkles({
        display: {
          desktop: 'inline-block',
          mobile: showOnMobile ? 'inline-block' : 'none',
          tablet: 'inline-block',
        },
      })
    )}
  />
)

export default Breadcrumbs
